// Explicit "Send invitation" control for an executor record (s91, 13a follow-up).
//
// Closes the loop on the onboarding invite deferral: naming an executor (in
// onboarding or this section) saves their details without auto-emailing them; the
// planner sends the invitation here, when ready. Rendered per-row inside the
// expandable executor drawer (Access & Permissions group) via the layout's
// `renderDrawerExtra` slot — only for rows that have a linked auth row (which means
// an email is present).
//
// States, driven by the auth row's status + invite_sent_at:
//   • accepted              → "Invitation accepted ✓" (joined; no action)
//   • pending, never sent   → "Send invitation"
//   • pending, already sent → "Invited ✓" + "Resend invitation"
// Small / muted by design (sage outline + taupe text) — not a primary CTA.

import { useMutation } from "@tanstack/react-query";
import { Check, Send, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSingleFlight } from "@/hooks/useSingleFlight";

export interface ExecutorInviteInfo {
  /** executor_access row id (the resend-invite target). */
  id: string;
  status: "pending" | "accepted" | "revoked";
  inviteSentAt?: string | null;
  email?: string | null;
}

/** Pure mapping from an executor's auth state to the control to show:
 *   • "none"     — no email yet (no auth row to invite) → render nothing
 *   • "accepted" — already joined → confirmation, no action
 *   • "send"     — pending, never emailed → "Send invitation"
 *   • "sent"     — pending, already emailed → "Invited ✓" + "Resend invitation"
 * Revoked is treated as "none" (no invite affordance on a revoked row). */
export function executorInviteState(executor: ExecutorInviteInfo): "none" | "accepted" | "send" | "sent" {
  if (!executor.email) return "none";
  if (executor.status === "revoked") return "none";
  if (executor.status === "accepted") return "accepted";
  return executor.inviteSentAt ? "sent" : "send";
}

/** s156 G (ruling 10): an invitation must not be sendable before the planner has chosen what this
 *  executor will be able to SEE. `access_level` is a real choice — "No access" is a valid, deliberate
 *  answer — so the gate is on having ANSWERED, not on the answer's content. Prevention only: a read-only
 *  sweep of staging AND prod found 0 invitations sent without an access level, so there is nothing to
 *  clean up; this stops the window from ever being used. */
export function accessLevelChosen(accessLevel: string | null | undefined): boolean {
  return (accessLevel ?? "").trim() !== "";
}

export function ExecutorInviteButton({
  clientId,
  executor,
  accessLevel,
}: {
  clientId: string;
  executor: ExecutorInviteInfo;
  /** this executor row's `access_level` value; null/empty → the invite is gated. */
  accessLevel?: string | null;
}) {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/clients/${clientId}/executors/${executor.id}/resend-invite`,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "executors"] });
      toast({
        title: "Invitation sent",
        description: "Your executor has been emailed an invitation to create their account.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't send invitation",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). Highest-stakes adoption in the sweep alongside MFA: the
  // route mints a NEW invite token and UPDATEs the row before emailing (executor-routes.ts), so a
  // double-click is not merely two emails — it is two emails where the FIRST one's link is already
  // dead. Both buttons below share one lock; they are the same action in two states.
  const sendFlight = useSingleFlight();
  const send = () => sendFlight.runQuiet(() => mutation.mutateAsync());

  const state = executorInviteState(executor);
  const busy = mutation.isPending || sendFlight.inFlight;

  // No email / revoked → no invite affordance.
  if (state === "none") return null;

  // Already joined — nothing to send.
  if (state === "accepted") {
    return (
      <p className="flex items-center gap-1.5 text-[13px] text-warm-taupe">
        <Check className="h-3.5 w-3.5 text-sage-600" />
        Invitation accepted
      </p>
    );
  }

  if (state === "send") {
    // s156 G (ruling 10): not sendable until an access level is chosen — the invitation is what opens
    // the door, so the planner decides what's behind it first. Short hint, no scolding.
    const gated = !accessLevelChosen(accessLevel);
    return (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={busy || gated}
          onClick={send}
          data-testid="executor-invite-send"
          className="inline-flex items-center gap-1.5 self-start rounded-md border border-sage-600/60 px-2.5 py-1 text-[13px] font-medium text-sage-600 hover:bg-sage-600/5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {busy ? "Sending…" : "Send invitation"}
        </button>
        {gated && (
          <p className="text-[12px] text-warm-taupe" data-testid="executor-invite-gate-hint">
            Choose an access level first — it controls what they'll be able to see.
          </p>
        )}
      </div>
    );
  }

  // Sent → confirmation + a quiet resend affordance.
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
      <span className="flex items-center gap-1.5 text-warm-taupe">
        <Check className="h-3.5 w-3.5 text-sage-600" />
        Invited
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={send}
        className="text-warm-taupe underline underline-offset-2 hover:text-foreground disabled:opacity-50"
      >
        {busy ? "Sending…" : "Resend invitation"}
      </button>
    </div>
  );
}
